/*
 * Het invullen van een contractsjabloon, los van NewEmployee.vue.
 *
 * De contracttekst is HTML: het sjabloon is HTML van HR, de tekst wordt met
 * v-html en in de editor (pell) getoond en gaat zo de PDF in. Wat er in een
 * sjabloon wordt ingevuld is daarentegen tekst: een naam, een straat, een
 * geboorteplaats, ook uit het externe formulier dat de medewerker zelf invult.
 * decodeTemplate() plakte die waarden onbewerkt in de HTML, dus een script in
 * een adresveld draaide in de sessie van de HR-gebruiker of werkgever die het
 * contract opende (opgeslagen XSS, H14 in de audit van 01-10-2026).
 *
 * De backend doet hetzelfde voor wat hij zelf invult (de [ VOORNAAM ]-velden,
 * ContractController::alsTekst met e()). Dubbel gaat het niet: een plek in de
 * tekst wordt of hier ingevuld, of er blijft een [ ... ]-aanduiding staan die
 * de backend later invult. Wat hier is ingevuld raakt de backend niet meer aan.
 */

/**
 * Maakt van een ingevulde waarde tekst voor in HTML.
 *
 * Dezelfde vijf tekens en dezelfde entiteiten als e() in de backend, zodat
 * dezelfde naam er in de opgeslagen tekst hetzelfde uitziet, wie hem ook
 * invulde. De aanhalingstekens gaan mee voor het geval een sjabloon een waarde
 * in een attribuut zet.
 *
 * Getallen, booleans en lege waarden blijven wat ze zijn: de groepen in een
 * sjabloon (<contracttype:Bepaalde tijd>) vergelijken die waarden, en er kan
 * geen HTML in zitten. Al het andere (een lijst, een object) wordt eerst tekst,
 * op dezelfde manier als replace() dat zou doen, en gaat dan door dezelfde
 * escape. Bleef een lijst ongemoeid, dan kwam de HTML erin er via die omweg
 * alsnog door.
 */
export function alsTekst(waarde){
  if(waarde === null || waarde === undefined || typeof waarde === 'number' || typeof waarde === 'boolean') return waarde;
  return String(waarde)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/*
 * Het onzichtbare teken (U+200B, een spatie zonder breedte) dat voor een
 * artikelnummer komt te staan, zodat setTemplate() en setArticleIndexes() in
 * NewEmployee.vue het nummer later terugvinden en kunnen hernummeren.
 *
 * Opgebouwd uit de code en niet letterlijk getypt: letterlijk is het niet te
 * zien, en een editor of formatter die het weghaalt breekt het hernummeren
 * zonder dat iemand het merkt.
 */
const ARTIKELMARKERING = String.fromCharCode(0x200B);

/**
 * Vult de variabelen (##naam##) van een sjabloon in met de waarden uit dict.
 *
 * Het sjabloon zelf blijft zoals het is; alleen de waarden gaan door alsTekst().
 * De rest is het gedrag dat decodeTemplate() al had:
 * - ##artikel## wordt het artikelnummer met een onzichtbare markering ervoor,
 *   zodat het later hernummerd kan worden;
 * - zonder editor wordt een lege of onleesbare waarde " [ NOG NIET BEPAALD ] ";
 * - met editor wordt een lege waarde (null) niets.
 *
 * @param {string} tekst het sjabloon of een deel ervan
 * @param {object} dict de waarden, zie setDict() in NewEmployee.vue
 * @param {{artikelnummer?: number, metEditor?: boolean}} [opties]
 */
export function vulWaardenIn(tekst, dict, opties){
  const artikelnummer = opties && opties.artikelnummer !== undefined ? opties.artikelnummer : 0;
  const metEditor = Boolean(opties && opties.metEditor);
  let uit = tekst;
  for(const gevonden of tekst.matchAll(/##([A-Za-z0-9_]+?)##/gs)){
    let waarde;
    if(gevonden[1] == 'artikel'){
      waarde = ARTIKELMARKERING + artikelnummer;
    }
    else{
      const ruw = dict[gevonden[1]];
      if(!metEditor)
        waarde = (ruw === null || ruw === '' || ruw === undefined || ruw === 'Invalid date') ? ' [ NOG NIET BEPAALD ] ' : alsTekst(ruw);
      else
        waarde = ruw === null ? '' : alsTekst(ruw);
    }
    // Als functie en niet als tekst: in een vervangtekst leest replace() een
    // dollarteken als verwijzing ($& is de gevonden tekst), en dit is invoer.
    uit = uit.replace(gevonden[0], () => waarde);
  }
  return uit;
}
